import {
  OBSERVATION_NODE,
  OBSERVATION_PANEL_NODE,
} from '@seen/core/observation-logic/documentUnits'
import { requiredGaps } from '@seen/core/observation-logic/noteCapture'
import { resourceBindingConditionValue } from '@seen/core/resourceBindingRegistry'
import { Extension } from '@tiptap/core'
import type { Node as PMNode } from '@tiptap/pm/model'
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state'
import {
  Decoration,
  type DecorationAttrs,
  DecorationSet,
} from '@tiptap/pm/view'
import { CONDITIONAL_DISPLAY_KEY } from '#src/components/rich-text-editor/conditional-display/ConditionalDisplayExtension.ts'
import { hiddenIds } from '#src/components/rich-text-editor/required/requiredGaps.ts'

export interface RequiredAnswersOptions {
  // Behind the structuredCapture flag; off, the extension does nothing.
  enabled: boolean
  // A template marks its required questions and never asks for answers.
  isTemplate: boolean
}

interface RequiredAnswersState {
  // A submit found gaps: they stay outlined until answered.
  flagged: boolean
  decorations: DecorationSet
}

export const REQUIRED_ANSWERS_KEY = new PluginKey<RequiredAnswersState>(
  'requiredAnswers'
)

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    requiredAnswers: {
      // Outlines every unanswered required question the note shows and
      // moves to the first.
      showRequiredGaps: () => ReturnType
    }
  }
}

// Every required observation node and panel moving in step with the doc, by
// position.
function requiredNodes(doc: PMNode): { pos: number; node: PMNode }[] {
  const found: { pos: number; node: PMNode }[] = []
  doc.descendants((node, pos) => {
    if (
      (node.type.name === OBSERVATION_NODE ||
        node.type.name === OBSERVATION_PANEL_NODE) &&
      node.attrs.required
    ) {
      found.push({ pos, node })
    }
    return true
  })
  return found
}

// The node's box draws the marks from these attributes: an asterisk inside
// its label (from the node's own `required`), and once a submit found it
// unanswered, an outline until it is answered or hidden.

export const RequiredAnswersExtension =
  Extension.create<RequiredAnswersOptions>({
    name: 'requiredAnswers',
    // Reads what the conditional display plugin hides, so its state must
    // exist first: a lower priority starts after the default.
    priority: 50,
    addOptions() {
      return { enabled: false, isTemplate: false }
    },
    addCommands() {
      return {
        showRequiredGaps:
          () =>
          ({ tr, dispatch, state }) => {
            if (!this.options.enabled) {
              return false
            }
            const contexts =
              this.editor.storage.registryVariable?.contexts ?? {}
            const [first] = requiredGaps(
              state.doc.toJSON(),
              hiddenIds(state),
              (id) => resourceBindingConditionValue(id, contexts)
            )
            if (dispatch) {
              tr.setMeta(REQUIRED_ANSWERS_KEY, { flagged: true })
              const target = requiredNodes(state.doc).find(
                ({ node }) => node.attrs.id === first?.id
              )
              if (target) {
                tr.setSelection(
                  TextSelection.near(tr.doc.resolve(target.pos))
                ).scrollIntoView()
              }
            }
            return true
          },
      }
    },
    addProseMirrorPlugins() {
      if (!this.options.enabled) {
        return []
      }
      const { isTemplate } = this.options
      const editor = this.editor
      const decorate = (
        doc: PMNode,
        flagged: boolean,
        hidden: ReadonlySet<string>
      ): DecorationSet => {
        const nodes = requiredNodes(doc)
        if (!nodes.length) {
          return DecorationSet.empty
        }
        const contexts = editor.storage.registryVariable?.contexts ?? {}
        const gaps =
          flagged && !isTemplate
            ? new Set(
                requiredGaps(doc.toJSON(), hidden, (id) =>
                  resourceBindingConditionValue(id, contexts)
                ).map((gap) => gap.id)
              )
            : new Set<string>()
        return DecorationSet.create(
          doc,
          nodes.flatMap(({ pos, node }) => {
            const id = node.attrs.id as string | null
            if (!isTemplate && id && hidden.has(id)) {
              return []
            }
            const gap = !!id && gaps.has(id)
            const attrs: DecorationAttrs = {
              'data-required-shown': '',
            }
            if (gap) {
              attrs['data-required-gap'] = ''
            }
            return [Decoration.node(pos, pos + node.nodeSize, attrs)]
          })
        )
      }
      return [
        new Plugin<RequiredAnswersState>({
          key: REQUIRED_ANSWERS_KEY,
          state: {
            init: (_, state) => ({
              flagged: false,
              decorations: decorate(state.doc, false, hiddenIds(state)),
            }),
            // Conditions settle in their own plugin's state, so every
            // transaction re-reads what is hidden; only notes with a
            // required question do any work.
            apply: (tr, previous, old, next) => {
              const meta = tr.getMeta(REQUIRED_ANSWERS_KEY) as
                | { flagged: boolean }
                | undefined
              if (
                !meta &&
                !tr.docChanged &&
                CONDITIONAL_DISPLAY_KEY.getState(old) ===
                  CONDITIONAL_DISPLAY_KEY.getState(next)
              ) {
                return previous
              }
              const flagged = meta?.flagged ?? previous.flagged
              return {
                flagged,
                decorations: decorate(next.doc, flagged, hiddenIds(next)),
              }
            },
          },
          props: {
            decorations: (state) =>
              REQUIRED_ANSWERS_KEY.getState(state)?.decorations,
          },
        }),
      ]
    },
  })
